export const DEFAULT_SAFE_URL_PROTOCOLS = ['http:', 'https:'] as const;

export type SafeUrlOptions = {
  allowedProtocols?: readonly string[];
  allowRelative?: boolean;
};

function normalizeAllowedProtocols(allowedProtocols: readonly string[]) {
  return allowedProtocols
    .filter((protocol) => protocol.endsWith(':'))
    .map((protocol) => protocol.toLowerCase());
}

function hasControlCharacter(url: string) {
  return [...url].some((character) => {
    const charCode = character.charCodeAt(0);
    return charCode <= 31 || charCode === 127;
  });
}

function isSafeRelativeUrl(url: string) {
  return (
    (url.startsWith('/') && !url.startsWith('//')) || url.startsWith('?') || url.startsWith('#')
  );
}

export function getSafeUrl(
  url: string | null | undefined,
  { allowedProtocols = DEFAULT_SAFE_URL_PROTOCOLS, allowRelative = false }: SafeUrlOptions = {}
): string | undefined {
  const trimmedUrl = url?.trim();
  if (!trimmedUrl || hasControlCharacter(trimmedUrl) || trimmedUrl.includes('\\')) {
    return undefined;
  }

  if (allowRelative && isSafeRelativeUrl(trimmedUrl)) {
    return trimmedUrl;
  }

  try {
    const parsedUrl = new URL(trimmedUrl);
    const normalizedAllowedProtocols = normalizeAllowedProtocols(allowedProtocols);

    if (
      normalizedAllowedProtocols.includes(parsedUrl.protocol) &&
      !parsedUrl.username &&
      !parsedUrl.password
    ) {
      return parsedUrl.href;
    }
  } catch {
    return undefined;
  }

  return undefined;
}
